import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, ButtonBase, Stack } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { Fragment } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { GenericComponentTestIds } from '../../../../utils/testIds/GenericComponent.testIds';
import { BreadcrumbItem, BreadcrumbLabel } from './breadcrumb.types';
import { getBreadcrumbTitle, truncateBreadcrumbLabel } from './breadcrumb.utils';

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  maxTitleChars?: number;
}

function Breadcrumb({ items, maxTitleChars }: BreadcrumbProps) {
  const { translate } = useTranslation();

  const translateLabel = (label: BreadcrumbLabel) => translate(label as LangEnum);

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isLink = !isLast && Boolean(item.onClick);
        const label = translateLabel(item.label);
        const displayLabel = isLast ? truncateBreadcrumbLabel(label, maxTitleChars) : label;
        const itemTestId = `${GenericComponentTestIds.BREADCRUMB_ITEM}-${index}`;

        return (
          <Fragment key={`${String(item.label)}-${index}`}>
            {index > 0 && (
              <Box sx={{ display: 'flex', flexShrink: 0, color: 'text.disabled' }}>
                <Icon icon={ChevronRight} size="md" />
              </Box>
            )}
            <Stack direction="row" sx={{ alignItems: 'center', gap: 0.25, minWidth: 0 }}>
              {isLink ? (
                <ButtonBase
                  onClick={item.onClick}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    minWidth: 0,
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  <Typography variant="subtitle2" color="inherit" noWrap testId={itemTestId}>
                    {label}
                  </Typography>
                </ButtonBase>
              ) : (
                <Typography
                  variant="subtitle2"
                  noWrap
                  title={getBreadcrumbTitle(label, displayLabel)}
                  testId={itemTestId}
                  sx={{ color: isLast ? 'text.primary' : 'text.secondary' }}
                >
                  {displayLabel}
                </Typography>
              )}
              {item.trailing}
            </Stack>
          </Fragment>
        );
      })}
    </Stack>
  );
}

export type { BreadcrumbProps };
export default Breadcrumb;
